import { useSearchParams } from "react-router-dom";
import FailedPayment from "./failed-payment";
import SuccessfulPayment from "./successful-payment";
import PendingPayment from "./pending-payment";
import CertificatePaymentResult from "@/features/platform-certificate/components/certificate-payment-result";
import type { CertificatePaymentPayload } from "@/models/certificate/PlatformCertificate";

export enum PurchaseSource {
  VITRIN = 1,
  MINIAPP_DIRECT = 2,
}

type PaymentPayload = {
  /** Never set on a course purchase — see {@link ResultPayload}. */
  kind?: undefined;
  // "Pending" is SnappPay-only: finalization not yet confirmed (rare). Don't
  // grant access and don't show it as failed.
  status: "Completed" | "Failed" | "Pending";
  purchase_source?: PurchaseSource;
  price?: number;
  mini_app_uuid?: string;
  course_id?: number;
  course_name?: string;
  factor_id: string;
  created_at: string;
  reason?: string;                  //from bank response: a text for failed reason
};

/**
 * A platform-certificate order returns to this same page — there is no separate
 * certificate result route — and is told apart only by `kind`.
 */
type ResultPayload = PaymentPayload | CertificatePaymentPayload;

const PURCHASE_SOURCE_LABELS: Record<PurchaseSource, string> = {
  [PurchaseSource.VITRIN]: "ویترین آموزیم",
  [PurchaseSource.MINIAPP_DIRECT]: "برنامک",
};

/** Decodes the gateway result `payload` (base64 of UTF-8 JSON). */
function decodeBase64Json<T extends object>(payload: string): T | null {
  try {
    const binaryString = atob(payload);
    const bytes = new Uint8Array(binaryString.length);

    for (let index = 0; index < binaryString.length; index += 1) {
      bytes[index] = binaryString.charCodeAt(index);
    }

    const json = new TextDecoder("utf-8").decode(bytes);
    const parsed: unknown = JSON.parse(json);

    if (!parsed || typeof parsed !== "object") {
      return null;
    }

    return parsed as T;
  } catch {
    return null;
  }
}

export default function PaymentResultPagePresenter() {
  const [searchParams] = useSearchParams();
  const payload = searchParams.get("payload");
  const data = payload ? decodeBase64Json<ResultPayload>(payload) : null;

  if (data?.kind === "certificate") {
    return (
      <div className="flex min-h-screen w-full items-center justify-center bg-secondary-background p-4">
        <CertificatePaymentResult payload={data} />
      </div>
    );
  }

  const isCompleted = data
    ? data.status === "Completed"
    : searchParams.get("status") === "success";
  const isPending = data?.status === "Pending";

  const purchaseSourceLabel =
    data?.purchase_source !== undefined
      ? PURCHASE_SOURCE_LABELS[data.purchase_source]
      : undefined;

  return (
    <div className="flex min-h-screen w-full items-center justify-center bg-secondary-background p-4">
      {isPending ? (
        <PendingPayment
          reason={data?.reason}
          courseName={data?.course_name}
          factorId={data?.factor_id}
          createdAt={data?.created_at}
          purchaseSource={purchaseSourceLabel}
        />
      ) : isCompleted ? (
        <SuccessfulPayment
          price={data?.price}
          courseName={data?.course_name}
          factorId={data?.factor_id}
          createdAt={data?.created_at}
          miniAppUuid={data?.mini_app_uuid}
          courseId={data?.course_id}
          purchaseSource={purchaseSourceLabel}
        />
      ) : (
        <FailedPayment
          reason={data?.reason}
          price={data?.price}
          courseName={data?.course_name}
          factorId={data?.factor_id}
          createdAt={data?.created_at}
          purchaseSource={purchaseSourceLabel}
        />
      )}
    </div>
  );
}
